import React, { useCallback, useEffect, useRef, useState } from "react";
import { GET_APPLICATION_INFO } from "../../../api/gql";

/* Actions */
import isKey from "../../../utilities/keyboard-events";
import { ReadOnly } from "../../common";
import ContinueButton from "../common/ContinueButton";
import { usePromisedLazyQuery } from "../../../state/store/customHooks";
import withQuery from "../wrappers/gql/withQuery";
import { VALIDATE_ADDRESS } from "./gql";
import { GOOGLE_ADDRESS_TYPES, STATES } from "./consts";
import { GENERIC_ERROR_MESSAGE, GQL_FETCH_POLICY_CACHE_AND_NETWORK_ONLY } from "../../../constants";

const AddressForm = ({ readOnly, _completeStep, data: applicationInfo }) => {
  let autocomplete;
  const initializedAddress = applicationInfo?.getApplicationInfo?.address;

  const [showBypassOption, setShowBypassOption] = useState(false);
  const [bypassVerification, setBypassVerification] = useState(false);
  const [errorGql, setErrorGql] = useState(null);
  const [errorAddress, setErrorAddress] = useState(null);
  const [errorCity, setErrorCity] = useState(null);
  const [errorState, setErrorState] = useState(null);
  const [errorZip, setErrorZip] = useState(null);
  const address2Ref = useRef(null);
  const [inputFieldChanged, setInputFieldChanged] = useState(false);

  const [addressFields, setAddressFields] = useState({
    address1: "",
    address2: "",
    locality: "",
    state: "",
    postcode: "",
  });

  const checkInputAgainstExisting = (e) => {
    const { name, value } = e.target;

    // no manditory field can be empty
    if (value === '' && name !== 'address2') { return false };

    // ensure our other values arent empty unless its the current field being changed
    const manditoryFields = ['address1', 'locality', 'state', 'postcode'];
    const ValidFields = manditoryFields.every((field) => {
      return addressFields[field] || field === name
    });

    if (!ValidFields) { return false }
    return true;
  }

  const addressFieldsInputOnChange = (e) => {
    setAddressFields({ ...addressFields, [e.target.name]: e.target.value });
    setInputFieldChanged(checkInputAgainstExisting(e))
    resetErrors();
  };

  const [checkValidAddress, { loading: checkValidAddressLoading }] =
    usePromisedLazyQuery(VALIDATE_ADDRESS);

  useEffect(() => {
    if (!readOnly) {
      const unloadCallback = (event) => {
        const e = event || window.event;
        // Cancel the event
        e.preventDefault();
        if (e)
          e.returnValue = ""; // Legacy method for cross browser support

        return ""; // Legacy method for cross browser support
      };

      window.addEventListener("beforeunload", unloadCallback);
      return () => window.removeEventListener("beforeunload", unloadCallback);
    }
  }, []);

  const autocompleteRef = useCallback((node) => {
    if (node !== null) {
      const initAutocomplete = () => {
        autocomplete = new window.google.maps.places.Autocomplete(node, {
          componentRestrictions: { country: ["us"] },
          fields: ["address_components"],
          types: ["address"],
        });
        // When the user selects an address from the drop-down, populate the
        // address fields in the form.
        autocomplete.addListener("place_changed", fillInAddress);
      };
      const fillInAddress = () => {
        // Get the place details from the autocomplete object.
        const place = autocomplete.getPlace();
        let address1 = "";
        let postcode = "";
        let state = "";
        let locality = "";

        // Get each component of the address from the place details,
        // and then fill-in the corresponding field on the form.
        // place.address_components are google.maps.GeocoderAddressComponent objects
        for (const component of place.address_components) {
          const componentType = component.types[0];

          switch (componentType) {
            case GOOGLE_ADDRESS_TYPES.STREET_NUMBER: {
              address1 = `${component.long_name} ${address1}`;
              break;
            }
            case GOOGLE_ADDRESS_TYPES.ROUTE: {
              address1 += component.short_name;
              break;
            }
            case GOOGLE_ADDRESS_TYPES.POSTAL_CODE: {
              postcode = component.long_name;
              break;
            }
            case GOOGLE_ADDRESS_TYPES.LOCALITY: {
              locality = component.long_name;
              break;
            }
            case GOOGLE_ADDRESS_TYPES.ADMINISTRATIVE_AREA_LEVEL_1: {
              state = component.short_name;
              break;
            }
          }
        }

        setAddressFields({
          address1,
          address2: "",
          state,
          locality,
          postcode,
        });
        setInputFieldChanged(true)
        
        address2Ref.current.focus();
      };

      initAutocomplete();
    }
  }, []);

  useEffect(() => {
    if (initializedAddress)
      setAddressFields({
        address1: initializedAddress.street,
        // address2 has been merged into address 1 in BE so don't load it up here
        // placeholder will show but oh well
        locality: initializedAddress.city,
        state: initializedAddress.state,
        postcode: initializedAddress.zip,
      });
  }, []);

  const resetErrors = () => {
    setErrorAddress("");
    setErrorCity("");
    setErrorState("");
    setErrorZip("");
    setErrorGql("");
  };

  const isFormValid = () => {
    resetErrors();
    let hasErrors = false;
    if (!addressFields.address1) {
      setErrorAddress("Address is missing.");
      hasErrors = true;
    }
    if (!addressFields.locality) {
      setErrorCity("City is missing.");
      hasErrors = true;
    }
    if (!addressFields.state) {
      setErrorState("State is missing.");
      hasErrors = true;
    }
    if (!addressFields.postcode) {
      setErrorZip("Zip is missing.");
      hasErrors = true;
    }

    if(hasErrors) { setInputFieldChanged(false); }
    return !hasErrors;
  };

  const onFormSubmission = async () => {
    if (readOnly) {
      _completeStep();
      return;
    }

    setShowBypassOption(false);

    if (!isFormValid()) {
      setBypassVerification(false);
      return;
    }

    const {
      address1: street1,
      address2: street2,
      locality: city,
      state,
      postcode: zip,
    } = addressFields;

    let response;
    try {
      response = await checkValidAddress({
        variables: {
          address: {
            street1,
            street2,
            city,
            state,
            zip,
          },
        },
      });
    } catch (err) {
      setErrorGql(GENERIC_ERROR_MESSAGE);
      return;
    }

    const { validateAddressV2 } = response;
    const { error } = validateAddressV2;

    // TODO: one day we should at least process some of these errors and handle them specifically. e.g. "Invalid Address: Unit number missing or invalid" should be setErrorAddress2("blah")
    setErrorGql(error);
    if (error){
      setInputFieldChanged(false);
      setShowBypassOption(true);
      // if not bypass and error, do not complete step;
      if (!bypassVerification) { return }
    }

    const { address1: street, address2: unit_number } = addressFields;

    _completeStep({
      street,
      unit_number,
      city,
      state,
      zip,
    }, bypassVerification);
  };

  return (
    <>
        <form id="address-form" action="" method="get" autoComplete="off">
          {!readOnly && (
            <>
              <p className="pb-2">We will ship your card to this address!</p>
              <p className="pb-2">P.O. boxes are not accepted.</p>
            </>
          )}

          <p className="hidden">
            <em>* = required field</em>
          </p>

          <div
            className="errorText w-10/12 pb-2 mx-auto"
            style={{ fontSize: 16 }}
          >
            <p>{errorAddress}</p>
            <p>{errorCity}</p>
            <p>{errorState}</p>
            <p>{errorZip}</p>
            <p>{errorGql}</p>
          </div>
          <ReadOnly readOnly={readOnly}>
            <label htmlFor="address1">
              {/* <!-- Avoid the word "address" in id, name, or label text to avoid browser autofill from conflicting with Place Autocomplete. Star or comment bug https://crbug.com/587466 to request Chromium to honor autoComplete="off" attribute. --> */}
              <span className="hidden">Address*</span>
              <input
                ref={autocompleteRef}
                id="address1"
                name="address1"
                value={addressFields.address1}
                onChange={addressFieldsInputOnChange}
                placeholder="Address"
                className={`inputField inputFieldText ${
                  errorGql || errorAddress ? "redBorder" : ""
                }`}
                required
                autoComplete="off"
              />
            </label>
            <label htmlFor="address2">
              <span className="hidden">Apartment, unit, suite, or floor #</span>
              <input
                ref={address2Ref}
                id="address2"
                name="address2"
                value={addressFields.address2}
                onChange={addressFieldsInputOnChange}
                placeholder="Apt/Suite/Unit # (if applicable)"
                className={`inputField inputFieldText ${
                  errorGql ? "redBorder" : ""
                }`}
              />
            </label>
            <label htmlFor="locality">
              <span className="hidden">City*</span>
              <input
                id="locality"
                name="locality"
                value={addressFields.locality}
                onChange={addressFieldsInputOnChange}
                placeholder="City"
                className={`inputField inputFieldText ${
                  errorGql || errorCity ? "redBorder" : ""
                }`}
                required
              />
            </label>
            <div className="flex space-x-4">
              <label className="flex-1" htmlFor="state">
                <span className="hidden">State*</span>
                <select
                  id="state"
                  name="state"
                  type="select"
                  placeholder="State"
                  value={addressFields.state}
                  onChange={addressFieldsInputOnChange}
                  style={{
                    padding: "11px 18px",
                    ...(!addressFields.state ? { color: "#a0aec0" } : {}),
                  }}
                  className={`inputField inputFieldText ${
                    errorGql || errorState ? "redBorder" : ""
                  }`}
                  disabled={readOnly}
                  required
                >
                  <option value="" disabled selected hidden>
                    State
                  </option>
                  {STATES.map((state) => (
                    <option value={state.value} key={state.value}>
                      {state.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex-1" htmlFor="postal_code">
                <span className="hidden">Postal code*</span>
                <input
                  id="postcode"
                  name="postcode"
                  value={addressFields.postcode}
                  onChange={addressFieldsInputOnChange}
                  placeholder="Zip code"
                  className={`inputField inputFieldText ${
                    errorGql || errorZip ? "redBorder" : ""
                  }`}
                  required
                />
              </label>
            </div>
          </ReadOnly>
        </form>
      {showBypassOption &&
        <div className="mt-2">
          <div className="text-sm mb-2 mx-auto w-10/12">
            <input
              type="checkbox"
              className="mr-2"
              checked={bypassVerification}
              onChange={(e) => {
                setBypassVerification(e.target.checked);
              }}
            />
            <span>
            I understand that if I choose to continue, my card may be
            shipped to an invalid address.
            </span>
          </div>
        </div>

      }

      <ContinueButton
        loading={checkValidAddressLoading}
        onClick={onFormSubmission}
        disabled={ !(bypassVerification || inputFieldChanged || readOnly) }
      />
    </>
  );
};

export default withQuery(GET_APPLICATION_INFO, GQL_FETCH_POLICY_CACHE_AND_NETWORK_ONLY)(AddressForm);
